import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import theme from '../style/theme'
import { vw } from '../style/vw'
import cn from 'classnames'

/**
 * ErrorPopup component to display error messages from Shopify API
 * @param {Object|null} props.error - Error object from Shopify API
 * @param {Function} props.onClose - Function to close the popup
 * @param {number} props.autoCloseTime - Time in milliseconds after which the popup will auto-close (default: 5000)
 */
const ErrorPopup = ({ error, onClose, autoCloseTime = 15000 }) => {
  const classes = useStyles()
  const [isVisible, setIsVisible] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (error) {
      // Parse the error object to extract relevant message
      let message = 'An error occurred'

      // Handle different error formats
      if (error.data && error.data.cartLinesAdd && error.data.cartLinesAdd.userErrors) {
        // Handle specific Shopify error format
        const userErrors = error.data.cartLinesAdd.userErrors
        if (userErrors.length > 0) {
          message = userErrors[0].message || message
        }
      } else if (error.message) {
        // Handle generic error object
        message = error.message
      } else if (typeof error === 'string') {
        // Handle string errors
        message = error
      }

      setErrorMessage(message)
      setIsVisible(true)

      // Auto-close after specified time
      const timer = setTimeout(() => {
        handleClose()
      }, autoCloseTime)

      return () => clearTimeout(timer)
    }
  }, [error, autoCloseTime])

  const handleClose = () => {
    setIsVisible(false)
    // Add a small delay before calling onClose to allow animation to complete
    setTimeout(() => {
      if (onClose) onClose()
    }, 300)
  }

  if (!isVisible) return null

  return (
    <div className={classes.overlay}>
      <div className={cn(classes.popup, { [classes.visible]: isVisible })}>
        <div className={classes.header}>
          <h3 className={classes.title}>ERROR</h3>
          <button className={classes.closeButton} onClick={handleClose}>×</button>
        </div>
        <div className={classes.content}>
          <p className={classes.message}>{errorMessage}</p>
        </div>
        <div className={classes.footer}>
          <button className={classes.okButton} onClick={handleClose}>OK</button>
        </div>
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    top: vw(10),
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: [0, vw(20)],
    [theme.breakpoints.up('md')]: {
      padding: [0, vw(20, 'desktop')]
    }
  },

  popup: {
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    width: '100%',
    maxWidth: vw(340),
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(20px)',
    opacity: 0,
    transition: 'transform 0.3s ease, opacity 0.3s ease',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      maxWidth: vw(400, 'desktop')
    }
  },

  visible: {
    transform: 'translateY(0)',
    opacity: 1
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: [vw(15), vw(20)],
    borderBottom: [1, 'solid', theme.colors.lightGrey || '#e4e4e4'],
    [theme.breakpoints.up('md')]: {
      padding: [vw(15, 'desktop'), vw(20, 'desktop')]
    }
  },

  title: {
    margin: 0,
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: vw(18),
    [theme.breakpoints.up('md')]: {
      fontSize: vw(18, 'desktop')
    }
  },

  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: vw(24),
    cursor: 'pointer',
    padding: [vw(5), vw(10)],
    color: theme.colors.darkGrey || '#666',
    '&:hover': {
      color: theme.colors.black
    },
    [theme.breakpoints.up('md')]: {
      fontSize: vw(24, 'desktop'),
      padding: [vw(5, 'desktop'), vw(10, 'desktop')]
    }
  },

  content: {
    padding: [vw(20)],
    [theme.breakpoints.up('md')]: {
      padding: [vw(20, 'desktop')]
    }
  },

  message: {
    margin: 0,
    fontFamily: theme.fonts.body,
    fontSize: vw(16),
    lineHeight: 1.5,
    color: theme.colors.dark || '#333',
    wordBreak: 'break-word',
    [theme.breakpoints.up('md')]: {
      fontSize: vw(16, 'desktop')
    }
  },

  footer: {
    padding: [vw(15), vw(20)],
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: [1, 'solid', theme.colors.lightGrey || '#e4e4e4'],
    [theme.breakpoints.up('md')]: {
      padding: [vw(15, 'desktop'), vw(20, 'desktop')]
    }
  },

  okButton: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    border: [1, 'solid', theme.colors.primary],
    padding: [vw(8), vw(20)],
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: vw(14),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.colors.white,
      color: theme.colors.primary
    },
    [theme.breakpoints.up('md')]: {
      padding: [vw(8, 'desktop'), vw(20, 'desktop')],
      fontSize: vw(14, 'desktop')
    }
  }
}, { name: 'ErrorPopup' })

export default ErrorPopup
